// Side 2 colour picker for the butterfly weft.
// A swatch dropdown (mirroring the Side 1 variant Colour option) whose choice is
// written to a hidden properties[...] input attached to the buy-buttons form, so it
// rides along with add-to-cart as a line-item property rather than a variant.
class ProductSecondColour extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-second-colour-input]');
    this.trigger = this.querySelector('[data-second-colour-trigger]');
    this.triggerInner = this.querySelector('[data-second-colour-trigger-inner]');
    this.panel = this.querySelector('[data-second-colour-panel]');
    this.error = this.querySelector('[data-second-colour-error]');
    this.options = Array.from(this.querySelectorAll('[data-value]'));
    this.required = this.dataset.required === 'true';

    if (!this.input || !this.trigger || !this.panel) return;

    this.onTriggerClick = () => this.toggle();
    this.onDocClick = (e) => {
      if (this.isOpen && !this.contains(e.target)) this.close();
    };
    this.onKeydown = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
        this.trigger.focus();
      }
    };

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.options.forEach((opt) => opt.addEventListener('click', () => this.select(opt)));
    document.addEventListener('click', this.onDocClick);
    this.addEventListener('keydown', this.onKeydown);

    // Block add-to-cart until a colour is chosen (capture phase runs before the
    // product form's own submit handler, so preventDefault actually stops it).
    this.form = this.dataset.formId ? document.getElementById(this.dataset.formId) : this.closest('form');
    if (this.required && this.form) {
      this.onSubmit = (e) => this.handleSubmit(e);
      this.form.addEventListener('submit', this.onSubmit, true);
    }
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    document.removeEventListener('click', this.onDocClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.form?.removeEventListener('submit', this.onSubmit, true);
  }

  get isOpen() {
    return !this.panel.hasAttribute('hidden');
  }

  open() {
    this.panel.removeAttribute('hidden');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.panel.setAttribute('hidden', '');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  select(opt) {
    this.input.value = opt.dataset.value || '';

    // Rebuild the trigger from the chosen option's swatch + name (clone, don't move).
    const swatch = opt.querySelector('.swatch');
    const name = opt.querySelector('.bw-colour-option__name');
    this.triggerInner.replaceChildren();
    if (swatch) this.triggerInner.appendChild(swatch.cloneNode(true));
    if (name) this.triggerInner.appendChild(name.cloneNode(true));

    this.options.forEach((o) => {
      const li = o.closest('[role="option"]');
      const selected = o === opt;
      o.classList.toggle('is-selected', selected);
      if (li) li.setAttribute('aria-selected', selected ? 'true' : 'false');
    });

    this.hideError();
    this.close();
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleSubmit(e) {
    if (this.input.value) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.showError();
    this.open();
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  showError() {
    this.error?.removeAttribute('hidden');
  }

  hideError() {
    this.error?.setAttribute('hidden', '');
  }
}

if (!customElements.get('product-second-colour')) {
  customElements.define('product-second-colour', ProductSecondColour);
}
